import React, { FC, useState } from 'react';
import { Input } from 'antd';
import { Button, Notification } from 'Components';
import styles from './TenantLogin.module.scss';
import { ReactComponent as EyeOpen } from '../../../../Icons/new-eye-oppened.svg';
import { ReactComponent as EyeCross } from '../../../../Icons/new-eye-crossed.svg';
import { CognitoUser } from '../pages/Login';
import { CognitoResponseError, SignInRequest } from 'Services/Api/Auth/Types';
import { PhoneNumber } from 'Pages/Auth/Auth.types';
import { useHistory } from 'react-router-dom';
import { useStores } from 'Hooks/useStore';
import {
	COGNITO_ERROR_MSG,
	COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP
} from 'app-config';
import { getAppRoutes } from 'Pages/App/App.constants';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import { firstLoginScreenSchema, loginSchema } from 'Pages/Auth/Login/schema';
import { translateTInfo } from 'i18n/i18n.helpers';

type LoginValues = SignInRequest;

type FirstLoginValues = SignInRequest & PhoneNumber;

type Values = LoginValues | FirstLoginValues;

interface TenantLoginProps {
	setShowResetPassModal: React.Dispatch<React.SetStateAction<boolean>>;
	firstTimeSignIn?: boolean;
	setCognitoUser(user: CognitoUser | null): void;
}

const TenantLogin: FC<TenantLoginProps> = ({
	setShowResetPassModal,
	firstTimeSignIn,
	setCognitoUser
}) => {
	const [showPass, setShowPass] = useState(false);
	const history = useHistory();
	const { authStore } = useStores();
	const { t } = useTranslation();

	const viewPass = () => setShowPass(true);
	const hidePass = () => setShowPass(false);

	const forgotPassword = () => setShowResetPassModal(true);

	const ssoCheck = window.location.search;
	const ssoParams = new URLSearchParams(ssoCheck);
	let deactivated = false;
	let newuser = false;
	if (ssoParams.has('sso')) {
		localStorage.clear();
		(async () => {
			const rawcreds = atob(ssoParams.get('sso')!);
			const creds = rawcreds.split(':');
			const ssologin: SignInRequest = {
				login: creds[0],
				password: creds[1]
			};
			const user = await authStore.signIn(ssologin);
			const url = getAppRoutes().HOME;
			const historyState = {
				userLoggedIn: true
			};
			history.push(url, historyState);
		})();
	}

	if (ssoParams.has('disabled')) {
		if (ssoParams.get('disabled') == 'true') {
			deactivated = true;
		}
	}

	if (ssoParams.has('newuser')) {
		if (ssoParams.get('newuser') == 'true') {
			newuser = true;
		}
	}

	let showsso = false;
	let ssolink = '';
	let ssoimage = '';
	let alttext = '';
	if (window.localStorage.getItem('tenantsso') == null) {
		window.localStorage.setItem('tenantsso', 'false');
	}

	const checksso = window.localStorage.getItem('tenantsso');
	if (checksso!.includes('"sso":tru')) {
		showsso = true;
	}

	if (showsso) {
		let env = 'prod';
		let tenant_id = [];
		const tenanturl = window.location.href;
		if (tenanturl.includes('stg.v') || tenanturl.includes('stg.sys')) {
			env = 'stage';
		}
		if (
			tenanturl.includes('dev.v') ||
			tenanturl.includes('dev.sys') ||
			tenanturl.includes(':300')
		) {
			env = 'dev';
		}
		tenant_id = window.location.href.split('/');
		const z = tenant_id.length - 2;
		ssolink =
			'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=1bd5cb44-e6d1-4d9d-bf81-dd9a012c59a2&response_type=code&redirect_uri=https%3A%2F%2Fauth-' +
			env +
			'.venzagrc.com%2Fsso%2Fvalidate&response_mode=form_post&scope=User.Read&state=' +
			tenant_id[z];
		ssoimage = 'https://api.venzagroup.com/images/sign-in-ms-button.svg';
		alttext = 'Sign-In with Microsoft';
		if (tenanturl.includes('kempinsk')) {
			ssolink =
				'https://dev-3h1groi6wr2vhd8r.us.auth0.com/authorize?response_type=code&scope=openid%20profile%20email&audience=https%3A%2F%2Fdev-3h1groi6wr2vhd8r.us.auth0.com%2Fapi%2Fv2%2F&client_id=lGsqmkWribOLe4w9xWZ7t6llugTTFPje&redirect_uri=https%3A%2F%2Fauth-' +
				env +
				'.venzagrc.com%2Fsso%2Fvalidate&state=' +
				tenant_id[z];
			ssoimage =
				'https://components.venzapeak.com/venza/images/okta-sso-button.png';
			alttext = 'Sign-In with OKTA';
		}
	}

	const onSubmit = async (values: Values, helpers: FormikHelpers<Values>) => {
		helpers.setStatus('');
		try {
			localStorage.clear();
			const user = await authStore.signIn(values);

			if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
				setCognitoUser(user);
			} else {
				const state = history.location.state as {
					returnUrl?: string;
				} | null;

				const url = state?.returnUrl ?? getAppRoutes().HOME;

				const historyState = {
					userLoggedIn: true
				};
				history.push(url, historyState);
			}
		} catch (e) {
			const error = e as CognitoResponseError;
			helpers.setSubmitting(false);

			if (
				error.message === COGNITO_ERROR_MSG.TEMPORARY_PASSWORD_EXPIRED
			) {
				history.push(getAppRoutes().RESET_TEMPORARY_PASSWORD, {
					cognitoLoginTemporaryPasswordExpired: values.login
				});
				return;
			}

			const cognitoError =
				COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP[error.message];

			if (cognitoError) {
				helpers.setStatus(t(cognitoError));
				return;
			}

			switch (error.code) {
				case 'NotAuthorizedException':
				case 'UserNotFoundException':
					helpers.setStatus(t('auth.login.errors.notAuthorized'));
					break;
				default:
					Notification.error({
						description: t('errorCodes.genericErrorMessage')
					});
					break;
			}
		}
	};

	const initialValues: Values = {
		login: '',
		password: ''
	};

	const schema = firstTimeSignIn ? firstLoginScreenSchema : loginSchema;

	const ErrorComponent = (
		<div className={styles.errorMsg}>
			<strong>Invalid username or password.</strong> Try again, reset your
			password, or contact your manager.
		</div>
	);

	return (
		<div className={styles.container}>
			<div className={styles.title}>Sign in</div>
			<Formik
				initialValues={initialValues}
				onSubmit={onSubmit}
				validationSchema={schema}
			>
				{(formik) => (
					<Form>
						<div className={styles.field}>
							<label>Username</label>
							<Input
								name="login"
								size="large"
								placeholder="Enter your username"
								className={styles.inputField}
								value={formik.values.login}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
							/>
							{formik.touched.login && formik.errors.login && (
								<div className={styles.errorMsg}>
									{translateTInfo(
										formik.errors.login as any,
										t
									)}
								</div>
							)}
						</div>

						<div className={styles.field}>
							<label>Password</label>
							<Input
								name="password"
								type={showPass ? 'text' : 'password'}
								placeholder="Enter your password"
								className={styles.innerInput}
								suffix={
									showPass ? (
										<EyeCross onClick={hidePass} />
									) : (
										<EyeOpen onClick={viewPass} />
									)
								}
								value={formik.values.password}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
							/>
							{formik.touched.password &&
								formik.errors.password && (
									<div className={styles.errorMsg}>
										{translateTInfo(
											formik.errors.password as any,
											t
										)}
									</div>
								)}
						</div>

						<Button
							type="primary"
							shape="round"
							htmlType="submit"
							loading={formik.isSubmitting}
						>
							Log in
						</Button>
						{formik.status && ErrorComponent}
						{ssolink && (
							<div
								style={{
									width: '100%',
									textAlign: 'center',
									paddingTop: '30px',
									paddingBottom: '15px'
								}}
							>
								<a href={ssolink}>
									<img src={ssoimage} alt={alttext} />
								</a>
							</div>
						)}
					</Form>
				)}
			</Formik>

			{!firstTimeSignIn && (
				<div className={styles.forgotPassword} onClick={forgotPassword}>
					Forgot your password?
				</div>
			)}
		</div>
	);
};

export default TenantLogin;
